/**
 * Reçu PDF download helper — calls the authenticated back-office endpoint.
 * NOTE: a public variant exists (`/api/paiements-public/:token/recu.pdf`)
 * but back-office uses the authed paiementId-based endpoint with the JWT.
 */
import { getToken } from "./auth";

/** Téléchargement PDF authentifié (JWT) — partagé reçus / fiches de rattachement. */
async function downloadPdfAuthed(url: string, filename: string) {
  const token = getToken();
  try {
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  } catch (err) {
    console.error("Failed to download PDF", err);
    // Best-effort: open in new tab — gateway may serve it via auth cookie/etc.
    window.open(url, "_blank");
  }
}

export function downloadRecuPdf(paiementId: string, reference: string) {
  return downloadPdfAuthed(
    `/api/paiements/${encodeURIComponent(paiementId)}/recu.pdf`,
    `recu-${reference}.pdf`,
  );
}

/** Fiche de rattachement d'UN élève (code + QR) — lot D 29/07/2026. */
export function downloadFicheRattachement(eleveId: string, matricule: string) {
  return downloadPdfAuthed(
    `/api/ecole/eleves/${encodeURIComponent(eleveId)}/rattachement-fiche.pdf`,
    `fiche-rattachement-${matricule}.pdf`,
  );
}

/** Planche des fiches de TOUTE une classe, à découper et distribuer. */
export function downloadFichesClasse(classeId: string, libelle: string) {
  return downloadPdfAuthed(
    `/api/ecole/classes/${encodeURIComponent(classeId)}/rattachement-fiches.pdf`,
    `fiches-rattachement-${libelle}.pdf`,
  );
}

/**
 * Browser CSV export — pure JS, no server roundtrip.
 */
export function downloadCsv(filename: string, rows: Array<Record<string, unknown>>) {
  if (!rows.length) {
    const blob = new Blob([""], { type: "text/csv;charset=utf-8" });
    triggerDownload(blob, filename);
    return;
  }
  const headers = Object.keys(rows[0]);
  const escape = (v: unknown) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [
    headers.join(";"),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(";")),
  ].join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  triggerDownload(blob, filename);
}

function triggerDownload(blob: Blob, filename: string) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
